export type ReportType = "APPRAISAL" | "CIM";

export type DataDependencySource = "user" | "system";

export interface DataDependency {
  type: "metric" | "method_result" | "research" | "field" | "document";
  key: string;
  minimumYears?: number;
  description: string;
  satisfiedByDocTypes?: string[];
  source: DataDependencySource;
}

export interface ReportSectionRequirement {
  section: string;
  label: string;
  importance: "critical" | "recommended" | "optional";
  dataDependencies: DataDependency[];
}

export interface ReportRequirement {
  reportType: ReportType;
  label: string;
  sections: ReportSectionRequirement[];
}

export const APPRAISAL_REPORT_REQUIREMENTS: ReportRequirement = {
  reportType: "APPRAISAL",
  label: "Appraisal Report",
  sections: [
    {
      section: "business_profile",
      label: "Business Profile",
      importance: "critical",
      dataDependencies: [
        { type: "field", key: "businessName", description: "Business name", source: "user" },
        { type: "field", key: "industry", description: "Industry classification", source: "user" },
        { type: "field", key: "state", description: "Business state/location", source: "user" },
      ],
    },
    {
      section: "financial_summary",
      label: "Financial Summary",
      importance: "critical",
      dataDependencies: [
        {
          type: "metric",
          key: "REVENUE",
          minimumYears: 1,
          description: "At least 1 year of revenue data",
          satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
          source: "user",
        },
        {
          type: "metric",
          key: "SDE",
          minimumYears: 1,
          description: "Seller's discretionary earnings",
          satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
          source: "user",
        },
      ],
    },
    {
      section: "valuation_methods",
      label: "Valuation Analysis",
      importance: "critical",
      dataDependencies: [
        {
          type: "method_result",
          key: "any",
          description: "At least one completed valuation method",
          source: "system",
        },
      ],
    },
    {
      section: "quality_of_earnings",
      label: "Quality of Earnings",
      importance: "recommended",
      dataDependencies: [
        {
          type: "metric",
          key: "REVENUE",
          minimumYears: 2,
          description: "At least 2 years of revenue for trend analysis",
          satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
          source: "user",
        },
        {
          type: "metric",
          key: "SDE",
          minimumYears: 2,
          description: "At least 2 years of SDE data",
          satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
          source: "user",
        },
      ],
    },
    {
      section: "dcf_analysis",
      label: "DCF & Sensitivity Analysis",
      importance: "recommended",
      dataDependencies: [
        {
          type: "method_result",
          key: "dcf",
          description: "DCF valuation completed",
          source: "system",
        },
        {
          type: "metric",
          key: "REVENUE",
          minimumYears: 2,
          description: "At least 2 years of historical data for DCF",
          satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
          source: "user",
        },
      ],
    },
    {
      section: "comparable_sales",
      label: "Comparable Sales Analysis",
      importance: "recommended",
      dataDependencies: [
        {
          type: "research",
          key: "comparables",
          description: "Comparable business sales data",
          source: "system",
        },
      ],
    },
    {
      section: "market_research",
      label: "Market & Industry Research",
      importance: "recommended",
      dataDependencies: [
        {
          type: "research",
          key: "market",
          description: "Market and industry research data",
          source: "system",
        },
      ],
    },
    {
      section: "deal_structures",
      label: "Deal Structures & SBA Feasibility",
      importance: "optional",
      dataDependencies: [
        {
          type: "method_result",
          key: "any",
          description: "At least one valuation method completed",
          source: "system",
        },
        {
          type: "field",
          key: "askingPrice",
          description: "Asking price set",
          source: "user",
        },
      ],
    },
  ],
};

export const CIM_REPORT_REQUIREMENTS: ReportRequirement = {
  reportType: "CIM",
  label: "Confidential Information Memorandum",
  sections: [
    {
      section: "cover_and_executive_summary",
      label: "Cover Page & Executive Summary",
      importance: "critical",
      dataDependencies: [
        { type: "field", key: "businessName", description: "Business name", source: "user" },
        { type: "field", key: "industry", description: "Industry classification", source: "user" },
        {
          type: "metric",
          key: "REVENUE",
          minimumYears: 1,
          description: "At least 1 year of revenue",
          satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
          source: "user",
        },
      ],
    },
    {
      section: "business_overview",
      label: "Business Overview",
      importance: "critical",
      dataDependencies: [
        { type: "field", key: "industry", description: "Industry classification", source: "user" },
        {
          type: "research",
          key: "market",
          description: "Industry/market research for context",
          source: "system",
        },
      ],
    },
    {
      section: "financial_summary",
      label: "Financial Summary",
      importance: "critical",
      dataDependencies: [
        {
          type: "metric",
          key: "REVENUE",
          minimumYears: 1,
          description: "At least 1 year of revenue",
          satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
          source: "user",
        },
      ],
    },
    {
      section: "transaction_overview",
      label: "Transaction Overview",
      importance: "critical",
      dataDependencies: [
        {
          type: "field",
          key: "askingPrice_or_valuation",
          description: "Asking price or completed valuation",
          source: "user",
        },
      ],
    },
    {
      section: "owner_biography",
      label: "Management Biography",
      importance: "optional",
      dataDependencies: [
        {
          type: "field",
          key: "teamMembers",
          description: "Team members discovered from data room + KYC research (at least 1)",
          source: "system",
        },
      ],
    },
    {
      section: "adjusted_financials",
      label: "Adjusted Financials & Normalization",
      importance: "recommended",
      dataDependencies: [
        {
          type: "metric",
          key: "SDE",
          minimumYears: 1,
          description: "SDE normalization data",
          satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS"],
          source: "user",
        },
        {
          type: "metric",
          key: "OWNER_COMPENSATION",
          minimumYears: 1,
          description: "Owner compensation details",
          satisfiedByDocTypes: ["TAX_RETURN", "PROFIT_LOSS", "W2_1099"],
          source: "user",
        },
      ],
    },
    {
      section: "customer_overview",
      label: "Customer Overview",
      importance: "recommended",
      dataDependencies: [
        {
          type: "research",
          key: "market",
          description: "Market research for customer insights",
          source: "system",
        },
      ],
    },
    {
      section: "growth_opportunities",
      label: "Growth Opportunities",
      importance: "recommended",
      dataDependencies: [
        {
          type: "research",
          key: "market",
          description: "Market research for growth analysis",
          source: "system",
        },
        {
          type: "method_result",
          key: "any",
          description: "At least one valuation completed for context",
          source: "system",
        },
      ],
    },
    {
      section: "investment_highlights",
      label: "Investment Highlights",
      importance: "recommended",
      dataDependencies: [
        {
          type: "method_result",
          key: "any",
          description: "Valuation results for investment context",
          source: "system",
        },
        {
          type: "research",
          key: "market",
          description: "Market research",
          source: "system",
        },
      ],
    },
    {
      section: "lease_summary",
      label: "Lease Summary",
      importance: "optional",
      dataDependencies: [
        {
          type: "document",
          key: "LEASE_AGREEMENT",
          description: "Lease or property document",
          satisfiedByDocTypes: ["LEASE_AGREEMENT", "OTHER_LEGAL"],
          source: "user",
        },
      ],
    },
    {
      section: "location_map",
      label: "Location & Map",
      importance: "optional",
      dataDependencies: [
        { type: "field", key: "state", description: "Business location", source: "user" },
      ],
    },
    {
      section: "team_overview",
      label: "Team Overview",
      importance: "optional",
      dataDependencies: [
        {
          type: "document",
          key: "EMPLOYEE_ROSTER",
          description: "Employee roster or org chart",
          satisfiedByDocTypes: ["ORG_CHART", "OTHER"],
          source: "user",
        },
      ],
    },
    {
      section: "detailed_financials",
      label: "Detailed Financial Statements",
      importance: "optional",
      dataDependencies: [
        {
          type: "metric",
          key: "REVENUE",
          minimumYears: 1,
          description: "Line-item financial detail",
          satisfiedByDocTypes: ["PROFIT_LOSS", "CHART_OF_ACCOUNTS"],
          source: "user",
        },
      ],
    },
  ],
};

export const REPORT_REQUIREMENTS: Record<ReportType, ReportRequirement> = {
  APPRAISAL: APPRAISAL_REPORT_REQUIREMENTS,
  CIM: CIM_REPORT_REQUIREMENTS,
};
